import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'

export interface RLSDebugInfo {
  timestamp: string
  auth: {
    uid: string | null
    is_authenticated: boolean
  }
  rls_functions: {
    get_user_agency_id: {
      result: string | null
      is_null: boolean
    }
    get_auth_account_id: {
      result: string | null
      is_null: boolean
    }
    get_jwt_account_id: {
      result: string | null
      is_null: boolean
    }
  }
  user_records: {
    agency_users: {
      exists: boolean
      data: {
        id: string
        auth_user_id: string
        agency_id: string
        name: string
        email: string
        created_at: string
        updated_at: string
      } | null
    }
    account_users: {
      exists: boolean
      data: {
        id: string
        auth_user_id: string
        account_id: string
        email: string
        name: string
        role: string
        is_active: boolean
        profile: string
        created_at: string
      } | null
    }
  }
  session: {
    has_active_session: boolean
    data: {
      id: string
      auth_user_id: string
      account_id: string
      agency_id: string
      expires_at: string
      created_at: string
    } | null
  }
  policy_simulation: {
    can_insert_account: boolean
    agency_id_for_insert: string | null
  }
}

export function useRLSDebug() {
  const [debugInfo, setDebugInfo] = useState<RLSDebugInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const fetchDebugInfo = async () => {
    try {
      setLoading(true)
      setError(null)

      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      console.log('🔍 [RLS Debug] Fetching RLS debug information...')

      const { data, error: rpcError } = await supabase.rpc('debug_get_rls_info')

      if (rpcError) {
        console.error('❌ [RLS Debug] Error fetching debug info:', rpcError)
        throw rpcError
      }

      console.log('✅ [RLS Debug] Debug info fetched successfully:', data)

      setDebugInfo(data as RLSDebugInfo)
      setLastUpdated(new Date())
    } catch (err) {
      console.error('❌ [RLS Debug] Failed to fetch debug info:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch debug information')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDebugInfo()
  }, [])

  const refresh = () => {
    fetchDebugInfo()
  }

  return {
    debugInfo,
    loading,
    error,
    lastUpdated,
    refresh
  }
}
